import { useState } from "react";
import { Link } from "react-router-dom";
import { BlogList } from "@/components/admin/BlogList";
import { BlogEditor } from "@/components/admin/BlogEditor";
import { BundleManager } from "@/components/admin/BundleManager";
import { AdminCourseAccessPanel } from "@/components/admin/AdminCourseAccessPanel";
import { AccessExtensionsPanel } from "@/components/admin/AccessExtensionsPanel";
import { BannerEditor } from "@/components/admin/BannerEditor";
import { LeadInbox } from "@/components/admin/LeadInbox";
import { NewsFeaturesManager } from "@/components/admin/NewsFeaturesManager";
import { SharingPanel } from "@/components/admin/SharingPanel";
import { VideoSlotEditor } from "@/components/admin/VideoSlotEditor";
import { PerformancesPanel } from "@/components/admin/PerformancesPanel";
import { cn } from "@/lib/utils";
import { routineVideoEnabled } from "@/lib/routineFeature";

const TABS = ["Blogs", "News & Features", "Signature Routines", "Final Video Editor", "Manage My Library", "Extend Access", "Banner", "Videos", "Leads", "Sharing"] as const;
type Tab = (typeof TABS)[number];

export function AdminPage() {
  const [tab, setTab] = useState<Tab>("Blogs");
  // Blog editor state: null = list view, string = editing that ID, "new" = creating
  const [editingBlogId, setEditingBlogId] = useState<string | "new" | null>(null);
  const usesSectionLayout = tab === "Leads" || tab === "Sharing" || tab === "Manage My Library" || tab === "Extend Access";

  return (
    <div className="min-h-screen bg-[#f7f5f3]">
      <div className="sticky top-0 z-30 border-b border-[#e7dee5] bg-[#f7f5f3]/95 shadow-[0_10px_30px_rgba(31,10,24,0.06)] backdrop-blur supports-[backdrop-filter]:bg-[#f7f5f3]/82">
        <header className="border-b border-[#ece5ea] bg-white/95 px-6 py-4 supports-[backdrop-filter]:bg-white/88">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
            <Link
              to="/app/dashboard/home"
              className="text-sm font-medium text-gray-500 transition-colors hover:text-pink-dark"
            >
              Back to Dashboard
            </Link>
          </div>
        </header>

        <div className="mx-auto w-full max-w-7xl px-6 py-4">
          <div className="flex gap-1 overflow-x-auto rounded-xl bg-white p-1 shadow-sm">
            {TABS.filter((t) => t !== "Final Video Editor" || routineVideoEnabled).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setEditingBlogId(null);
                }}
                className={cn(
                  "flex-1 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors",
                  tab === t
                    ? "bg-pink-dark text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-6 py-8">

        {/* Tab content */}
        <div className={usesSectionLayout ? "space-y-6" : "rounded-xl bg-white p-6 shadow-sm"}>
          {tab === "Blogs" &&
            (editingBlogId !== null ? (
              <BlogEditor
                blogId={editingBlogId === "new" ? null : editingBlogId}
                onBack={() => setEditingBlogId(null)}
              />
            ) : (
              <BlogList
                onEdit={(id) => setEditingBlogId(id)}
                onNew={() => setEditingBlogId("new")}
              />
            ))}

          {tab === "News & Features" && <NewsFeaturesManager />}
          {tab === "Signature Routines" && <BundleManager />}
          {routineVideoEnabled && tab === "Final Video Editor" && <PerformancesPanel />}
          {tab === "Manage My Library" && <AdminCourseAccessPanel />}
          {tab === "Extend Access" && <AccessExtensionsPanel />}
          {tab === "Banner" && <BannerEditor />}
          {tab === "Videos" && <VideoSlotEditor />}
          {tab === "Leads" && <LeadInbox />}
          {tab === "Sharing" && <SharingPanel />}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
